/**
 * Alshallal Pastry Company HR System - Document Expiry Alerts Widget
 * Phase 24: Segregated Employee and Company Document Alerts
 * 
 * Shows documents approaching or past expiry date
 * Employee documents and Company documents shown in separate sections
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  ChevronRight,
  ChevronLeft,
  FileText,
  RefreshCw,
  Bell,
  Building2,
  Users
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { daysUntilExpiry } from '@/lib/expiry';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Urgency tier styling
const URGENCY_STYLES = {
  expired: {
    bg: 'bg-red-50 border-red-200',
    badge: 'bg-red-100 text-red-700 border-red-200',
    icon: 'text-red-500',
    label: { en: 'Expired', ar: 'منتهي' }
  },
  critical: {
    bg: 'bg-amber-50 border-amber-200',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: 'text-amber-500',
    label: { en: '0-7 days', ar: '0-7 أيام' }
  },
  warning: {
    bg: 'bg-yellow-50 border-yellow-200',
    badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: 'text-yellow-500',
    label: { en: '8-30 days', ar: '8-30 يوم' }
  }
};

const ExpiryAlertsWidget = ({ 
  compact = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const { token, hasRole } = useAuth();
  const { isRTL } = useLanguage();
  
  // State for employee documents
  const [employeeAlerts, setEmployeeAlerts] = useState([]);
  const [employeeExpired, setEmployeeExpired] = useState([]);
  
  // State for company documents
  const [companyAlerts, setCompanyAlerts] = useState([]);
  const [companyExpired, setCompanyExpired] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [threshold, setThreshold] = useState('30');
  const [activeTab, setActiveTab] = useState('employee');

  const ArrowIcon = isRTL ? ChevronLeft : ChevronRight;

  // Only show for HR roles
  const canView = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');

  // Fetch alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      if (!canView) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch employee and company documents in parallel
        const [empAlertsRes, empExpiredRes, compAlertsRes, compExpiredRes] = await Promise.all([
          axios.get(`${API_URL}/documents/alerts/expiring?threshold_days=${threshold}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/documents/alerts/expired`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/company-documents/alerts/expiring?threshold_days=${threshold}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { alerts: [] } })),
          axios.get(`${API_URL}/company-documents/alerts/expired`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { documents: [] } }))
        ]);

        setEmployeeAlerts(empAlertsRes.data.alerts || []);
        setEmployeeExpired(empExpiredRes.data.documents || []);
        setCompanyAlerts(compAlertsRes.data.alerts || []);
        setCompanyExpired(compExpiredRes.data.documents || []);
      } catch (err) {
        console.error('Failed to fetch expiry alerts:', err);
        setError(err.response?.data?.detail?.error || 'Failed to load alerts');
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, [token, threshold, canView]);

  if (!canView) return null;

  const getUrgencyTier = (daysUntil) => {
    if (daysUntil < 0) return 'expired';
    if (daysUntil <= 7) return 'critical';
    return 'warning';
  };

  // Process employee alerts
  const allEmployeeAlerts = [
    ...employeeExpired.map(doc => ({
      ...doc,
      document_id: doc.id,
      employee_name: doc.employee_name || 'Unknown',
      document_type: doc.document_type_name || 'Document',
      days_until_expiry: typeof doc.days_until_expiry === 'number'
        ? doc.days_until_expiry
        : daysUntilExpiry(doc.expiry_date),
      urgency: 'expired',
      scope: 'employee'
    })),
    ...employeeAlerts.map(alert => ({
      ...alert,
      urgency: getUrgencyTier(alert.days_until_expiry),
      scope: 'employee'
    }))
  ].sort((a, b) => a.days_until_expiry - b.days_until_expiry);

  // Process company alerts
  const allCompanyAlerts = [
    ...companyExpired.map(doc => ({
      ...doc,
      document_id: doc.id,
      document_type: doc.document_type_name || doc.display_name || 'Company Document',
      days_until_expiry: typeof doc.days_until_expiry === 'number'
        ? doc.days_until_expiry
        : daysUntilExpiry(doc.expiry_date),
      urgency: 'expired',
      scope: 'company'
    })),
    ...companyAlerts.map(alert => ({
      ...alert,
      urgency: getUrgencyTier(alert.days_until_expiry),
      scope: 'company'
    }))
  ].sort((a, b) => a.days_until_expiry - b.days_until_expiry);

  // Calculate stats for each section
  const employeeStats = {
    expired: allEmployeeAlerts.filter(a => a.urgency === 'expired').length,
    critical: allEmployeeAlerts.filter(a => a.urgency === 'critical').length,
    warning: allEmployeeAlerts.filter(a => a.urgency === 'warning').length,
    total: allEmployeeAlerts.length
  };

  const companyStats = {
    expired: allCompanyAlerts.filter(a => a.urgency === 'expired').length,
    critical: allCompanyAlerts.filter(a => a.urgency === 'critical').length,
    warning: allCompanyAlerts.filter(a => a.urgency === 'warning').length,
    total: allCompanyAlerts.length
  };

  const totalStats = {
    expired: employeeStats.expired + companyStats.expired,
    critical: employeeStats.critical + companyStats.critical,
    warning: employeeStats.warning + companyStats.warning,
    total: employeeStats.total + companyStats.total
  };

  const handleViewDocument = (alert) => {
    if (alert.scope === 'company') {
      navigate(`/documents/company`);
    } else {
      navigate(`/employees?highlight=${alert.employee_id}`);
    }
  };

  const handleViewAll = () => {
    if (activeTab === 'company') {
      navigate('/documents/company');
    } else {
      navigate('/employees');
    }
  };

  const formatExpiryStatus = (days) => {
    if (days < 0) {
      const absDays = Math.abs(days);
      return isRTL ? `${absDays}ي متأخر` : `${absDays}d overdue`;
    }
    if (days === 0) return isRTL ? 'اليوم' : 'TODAY';
    return isRTL ? `${days}ي متبقي` : `${days}d left`;
  };

  // Render stats badges
  const renderStatsBadges = (stats) => (
    <div className="flex flex-wrap gap-2">
      {stats.expired > 0 && (
        <Badge className={URGENCY_STYLES.expired.badge}>
          <AlertTriangle className="h-3 w-3 me-1" />
          {stats.expired} {isRTL ? 'منتهي' : 'Expired'}
        </Badge>
      )}
      {stats.critical > 0 && (
        <Badge className={URGENCY_STYLES.critical.badge}>
          <Clock className="h-3 w-3 me-1" />
          {stats.critical} {isRTL ? '0-7 أيام' : '0-7 days'}
        </Badge>
      )}
      {stats.warning > 0 && (
        <Badge className={URGENCY_STYLES.warning.badge}>
          <AlertCircle className="h-3 w-3 me-1" />
          {stats.warning} {isRTL ? '8-30 يوم' : '8-30 days'}
        </Badge>
      )}
    </div>
  );

  // Render document list
  const renderDocumentList = (alerts, showEmployee = true) => (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {alerts.slice(0, 10).map((alert, idx) => {
        const urgencyStyle = URGENCY_STYLES[alert.urgency];
        return (
          <div
            key={alert.document_id || idx}
            className={cn(
              "p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow",
              urgencyStyle.bg
            )}
            onClick={() => handleViewDocument(alert)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {alert.document_type}
                </p>
                {showEmployee && alert.employee_name && (
                  <p className="text-xs text-muted-foreground truncate">
                    {alert.employee_name}
                  </p>
                )}
              </div>
              <Badge className={cn("shrink-0", urgencyStyle.badge)}>
                {formatExpiryStatus(alert.days_until_expiry)}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(new Date(alert.expiry_date), 'MMM d, yyyy')}
            </p>
          </div>
        );
      })}
      {alerts.length > 10 && (
        <p className="text-xs text-center text-muted-foreground py-2">
          {isRTL ? `و ${alerts.length - 10} مستند آخر...` : `...and ${alerts.length - 10} more documents`}
        </p>
      )}
    </div>
  );

  // Compact version for dashboard
  if (compact) {
    return (
      <Card className={cn("bg-white border-slate-200", className)} data-testid="expiry-alerts-widget-compact">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardDescription className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              {isRTL ? 'تنبيهات انتهاء الصلاحية' : 'Expiry Alerts'}
            </CardDescription>
            {totalStats.total > 0 && (
              <Badge variant="destructive" className="bg-red-100 text-red-700">
                {totalStats.total}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : totalStats.total === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">
              {isRTL ? 'لا توجد تنبيهات' : 'No expiry alerts'}
            </p>
          ) : (
            <div className="space-y-2">
              {renderStatsBadges(totalStats)}
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2"
                onClick={handleViewAll}
              >
                {isRTL ? 'عرض الكل' : 'View All'}
                <ArrowIcon className="h-4 w-4 ms-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full version with tabs
  return (
    <Card className={cn("bg-white border-slate-200", className)} data-testid="expiry-alerts-widget">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {isRTL ? 'تنبيهات انتهاء الصلاحية' : 'Document Expiry Alerts'}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'مستندات تحتاج إلى تجديد' : 'Documents requiring renewal'}
            </CardDescription>
          </div>
          <Select value={threshold} onValueChange={setThreshold}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{isRTL ? '7 أيام' : '7 days'}</SelectItem>
              <SelectItem value="14">{isRTL ? '14 يوم' : '14 days'}</SelectItem>
              <SelectItem value="30">{isRTL ? '30 يوم' : '30 days'}</SelectItem>
              <SelectItem value="60">{isRTL ? '60 يوم' : '60 days'}</SelectItem>
              <SelectItem value="90">{isRTL ? '90 يوم' : '90 days'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">{error}</p>
          </div>
        ) : totalStats.total === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>{isRTL ? 'لا توجد مستندات تنتهي صلاحيتها قريبًا' : 'No documents expiring soon'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary stats cards */}
            <div className="grid grid-cols-3 gap-2">
              <div className={cn("p-3 rounded-lg border text-center", URGENCY_STYLES.expired.bg)}>
                <p className="text-2xl font-bold text-red-700">{totalStats.expired}</p>
                <p className="text-xs text-red-600">{isRTL ? 'منتهي' : 'Expired'}</p>
              </div>
              <div className={cn("p-3 rounded-lg border text-center", URGENCY_STYLES.critical.bg)}>
                <p className="text-2xl font-bold text-amber-700">{totalStats.critical}</p>
                <p className="text-xs text-amber-600">{isRTL ? '0-7 أيام' : '0-7 days'}</p>
              </div>
              <div className={cn("p-3 rounded-lg border text-center", URGENCY_STYLES.warning.bg)}>
                <p className="text-2xl font-bold text-yellow-700">{totalStats.warning}</p>
                <p className="text-xs text-yellow-600">{isRTL ? '8-30 يوم' : '8-30 days'}</p>
              </div>
            </div>

            {/* Tabs for Employee vs Company */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="employee" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {isRTL ? 'مستندات الموظفين' : 'Employee Docs'}
                  {employeeStats.total > 0 && (
                    <Badge variant="secondary" className="ms-1">{employeeStats.total}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="company" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {isRTL ? 'مستندات الشركة' : 'Company Docs'}
                  {companyStats.total > 0 && (
                    <Badge variant="secondary" className="ms-1">{companyStats.total}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="employee" className="mt-4">
                {employeeStats.total === 0 ? (
                  <div className="text-center py-4 text-slate-500">
                    <p className="text-sm">{isRTL ? 'لا توجد تنبيهات' : 'No employee document alerts'}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">{renderStatsBadges(employeeStats)}</div>
                    {renderDocumentList(allEmployeeAlerts, true)}
                  </>
                )}
              </TabsContent>

              <TabsContent value="company" className="mt-4">
                {companyStats.total === 0 ? (
                  <div className="text-center py-4 text-slate-500">
                    <p className="text-sm">{isRTL ? 'لا توجد تنبيهات' : 'No company document alerts'}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">{renderStatsBadges(companyStats)}</div>
                    {renderDocumentList(allCompanyAlerts, false)}
                  </>
                )}
              </TabsContent>
            </Tabs>

            {/* View all button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleViewAll}
            >
              {isRTL 
                ? (activeTab === 'company' ? 'عرض جميع مستندات الشركة' : 'عرض جميع مستندات الموظفين')
                : (activeTab === 'company' ? 'View All Company Documents' : 'View All Employee Documents')
              }
              <ArrowIcon className="h-4 w-4 ms-2" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpiryAlertsWidget;
